import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/shared/AnimatedSection";

import { asset } from "@/lib/assets";
const approvalHeader = asset("case-studies/ClientApproval/approvalheader.png");
const docusignImg = asset("case-studies/ClientApproval/docusign.png");
const loOnboardingImg = asset("case-studies/ClientApproval/LOonboarding.png");
const sliderVideo = asset("case-studies/ClientApproval/Sliderinteraction.mp4");
const shareExperienceImg = asset("case-studies/ClientApproval/shareexperience.png");
const endToEndImg = asset("case-studies/ClientApproval/endtoend.png");
const mazeImg = asset("case-studies/ClientApproval/Maze.png");

// ─── Image helpers ────────────────────────────────────────────────────────────

function ImageSlot({
  src,
  alt,
  className = "",
  caption,
}: {
  src: string;
  alt: string;
  className?: string;
  caption?: string;
}) {
  const [lightbox, setLightbox] = useState(false);

  return (
    <>
      <div className="w-full">
        <div
          className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl overflow-hidden flex items-center justify-center cursor-zoom-in ${className}`}
          onClick={() => setLightbox(true)}
          title="Click to expand"
        >
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            onError={(e) => {
              const el = e.currentTarget as HTMLImageElement;
              el.style.display = "none";
              const parent = el.parentElement;
              if (parent && !parent.querySelector(".placeholder-label")) {
                const label = document.createElement("p");
                label.className =
                  "placeholder-label text-xs text-[#888888] text-center p-4";
                label.textContent = alt;
                parent.appendChild(label);
              }
            }}
          />
        </div>
        {caption && (
          <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">
            {caption}
          </p>
        )}
      </div>

      {lightbox &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setLightbox(false)}
          >
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setLightbox(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

function VideoSlot({
  src,
  className = "",
  caption,
}: {
  src: string;
  className?: string;
  caption?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="w-full">
      <div
        className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl overflow-hidden ${className}`}
      >
        <video
          ref={videoRef}
          src={src}
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        />
      </div>
      {caption && (
        <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">
          {caption}
        </p>
      )}
    </div>
  );
}

function PlaceholderSlot({
  label,
  className = "",
  caption,
}: {
  label: string;
  className?: string;
  caption?: string;
}) {
  return (
    <div className="w-full">
      <div
        className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl flex items-center justify-center ${className}`}
      >
        <p className="text-xs text-[#888888] text-center p-6">{label}</p>
      </div>
      {caption && (
        <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">
          {caption}
        </p>
      )}
    </div>
  );
}

// ─── Side nav ────────────────────────────────────────────────────────────────

const NAV_SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "problem", label: "The Problem" },
  { id: "approach", label: "Approach" },
  { id: "result", label: "Result" },
  { id: "usability-testing", label: "Usability Testing" },
  { id: "takeaways", label: "Takeaways" },
];

function SideNav() {
  const [active, setActive] = useState<string>(NAV_SECTIONS[0].id);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visibleSections = new Map<string, number>();

    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            visibleSections.set(id, entry.intersectionRatio);
          } else {
            visibleSections.delete(id);
          }
          if (visibleSections.size > 0) {
            const topmost = [...visibleSections.entries()].reduce((a, b) =>
              a[1] >= b[1] ? a : b,
            );
            setActive(topmost[0]);
          }
        },
        { threshold: [0, 0.25, 0.5], rootMargin: "-10% 0px -60% 0px" },
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <nav className="hidden xl:flex flex-col gap-1 sticky top-32 self-start w-48 shrink-0">
      {/* Project card */}
      <div className="mb-6 pb-6 border-b border-[#E2E8F0]">
        <p className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] text-sm leading-tight mb-0.5">
          HomeLight
        </p>
        <p className="text-xs text-[#888888] leading-snug">
          Loan pre-approval review
          <br />
          Buy Before You Sell
        </p>
      </div>

      {NAV_SECTIONS.map(({ id, label }) => (
        <a
          key={id}
          href={`#${id}`}
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById(id)
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className={`text-xs font-medium py-1.5 pl-3 border-l-2 transition-all duration-200 ${
            active === id
              ? "border-[#1D3FE8] text-[#1A1A1A]"
              : "border-transparent text-[#888888] hover:text-[#1A1A1A] hover:border-[#E2E8F0]"
          }`}
        >
          {label}
        </a>
      ))}
    </nav>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function CaseStudyApproval() {
  return (
    <div className="min-h-screen bg-[#F7F8FA] font-[DM_Sans]">
      {/* Sticky header */}
      <header className="sticky top-0 z-50 bg-[#F7F8FA]/90 backdrop-blur-sm border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
          <Link
            to="/#work"
            className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
          >
            ← All work
          </Link>
          <Link
            to="/"
            className="font-[Bricolage_Grotesque] font-bold text-sm text-[#1A1A1A]"
          >
            Verónica <span className="text-[#1D3FE8]">Aguilar</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12 pt-24 pb-20">
        <AnimatedSection>
          <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#888888] mb-4">
            HomeLight · Buy Before You Sell (Equity loan product)
          </p>
          <h1 className="font-[Bricolage_Grotesque] font-extrabold tracking-[-0.04em] text-[clamp(2.25rem,5vw,3.75rem)] text-[#1A1A1A] leading-[1.05] mb-6">
            <span className="text-[#1D3FE8]">Improving</span> loan pre-approval experience
          </h1>
          <p className="text-[#555] text-lg leading-[1.8] max-w-[680px] mb-12">
            We redesigned the borrower approval flow to replace a static
            DocuSign experience with a guided, mobile-first flow. By adding
            context, education, and a clearer signing interaction, we improved
            user understanding and increased conversion from approved to signed.
          </p>

          {/* Meta strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8 border-t border-[#E2E8F0]">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                Role
              </p>
              <p className="text-[0.9375rem] font-medium text-[#1A1A1A] leading-relaxed">
                Led UX and UI decisions. Ran user testing and partnered with
                engineering on handoff and implementation.
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                Team
              </p>
              <p className="text-[0.9375rem] font-medium text-[#1A1A1A] leading-relaxed">
                Product Owner (Gaurav) · Product Designer (me) · Engineering
                team
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>

      {/* Header image */}
      <AnimatedSection>
        <div className="max-w-5xl mx-auto px-6 sm:px-12 pb-20">
          <ImageSlot
            src={approvalHeader}
            alt="Approval case study header"
            className="w-full aspect-[16/7]"
          />
        </div>
      </AnimatedSection>

      {/* Sections */}
      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        <div className="flex gap-16 items-start">
          <SideNav />
          <div className="flex-1 min-w-0">

            {/* Overview */}
            <AnimatedSection
              id="overview"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Overview
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                A loan product where trust is the product.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                Buy Before You Sell is a loan product where Loan Officers (LOs)
                guide clients through financing options. While LOs are the
                primary users, borrowers are required to review and approve key
                steps — like pre-qualification terms.
              </p>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10">
                Previously, this process relied on a simple DocuSign email send
                to the borrowers with little context or onboarding. Resulting in
                multiple phone calls with their Loan Officer and our operations
                team answering the same product questions.
              </p>
              <ImageSlot
                src={docusignImg}
                alt="DocuSign experience before redesign"
                className="w-full aspect-[16/8]"
                caption="The previous DocuSign experience"
              />
            </AnimatedSection>

            {/* The Problem */}
            <AnimatedSection
              id="problem"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                The Problem
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                Signing without understanding.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6">
                Borrowers were asked to sign without understanding:
              </p>
              <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8 space-y-1 pl-1">
                <li>Loan terms</li>
                <li>Program details</li>
                <li>What "approval" actually meant</li>
              </ul>

              {/* Callout */}
              <div className="bg-[#F5C542] rounded-2xl px-6 py-5 max-w-[640px]">
                <p className="text-[#1A1A1A] font-semibold text-[0.9375rem] leading-[1.7]">
                  Low confidence, lower conversion.
                </p>
              </div>
            </AnimatedSection>

            {/* Approach */}
            <AnimatedSection
              id="approach"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Approach
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                A communication problem, not just a signing step.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-12">
                We reframed this as both a communication and experience problem.
              </p>

              {/* Step 1 */}
              <div className="mb-12">
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  1. Add context before action
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-4">
                  We introduced an onboarding layer before pre-approvals:
                </p>
                <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8 space-y-1 pl-1">
                  <li>Introductory email for the borrower when the Loan Officer submits their application</li>
                  <li>Optional for Loan Officers to send (keeps flexibility)</li>
                  <li>Sets expectations and explains the process</li>
                </ul>
                <ImageSlot
                  src={loOnboardingImg}
                  alt="LO onboarding opt-in and introductory email"
                  className="w-full aspect-[16/8]"
                />
              </div>

              {/* Step 2 */}
              <div className="mb-12">
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  2. Replace DocuSign with a guided flow
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-4">
                  Instead of sending borrowers to a static DocuSign experience, we
                  designed a mobile-first approval flow. Key changes:
                </p>
                <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8 space-y-1 pl-1">
                  <li>Step-by-step walkthrough of loan terms</li>
                  <li>Clear breakdown of amounts and program details</li>
                  <li>Progressive disclosure to avoid overload</li>
                </ul>
                <ImageSlot
                  src={endToEndImg}
                  alt="End-to-end user flow"
                  className="w-full aspect-[180/137]"
                  caption="End-to-end user flow"
                />
              </div>

              {/* Step 3 */}
              <div className="mb-12">
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  3. Design for confidence and clarity
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-4">
                  We introduced a more intentional interaction model:
                </p>
                <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-6 space-y-1 pl-1">
                  <li>
                    "Slide to approve" on mobile (checkbox on web)
                  </li>
                  <li>Reinforces commitment and reduces accidental actions</li>
                  <li>
                    Makes the moment of approval feel clear and deliberate
                  </li>
                </ul>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                  We also ensured e-signature compliance (timestamp, IP, etc.)
                  and automatic PDF generation and storage post-sign.
                </p>
                <div className="flex justify-center">
                  <VideoSlot
                    src={sliderVideo}
                    className="w-full max-w-[320px] aspect-[744/1432]"
                    caption="Slide to approve interaction"
                  />
                </div>
              </div>

              {/* Step 4 */}
              <div>
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  4. Improve Loan Officer experience for engagement
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-4">
                  Loan Officers need to have in control with their clients
                  communication. So, to make sure they feel comfortable sharing
                  this experience we updated their "Review and send
                  pre-approval" flow.
                </p>
                <ul className="list-disc list-inside text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8 space-y-1 pl-1">
                  <li>Preview of the exact client experience before sending</li>
                  <li>Allow them to add notes for their client</li>
                  <li>Review terms and amounts in their portal</li>
                </ul>
                <ImageSlot
                  src={shareExperienceImg}
                  alt="Loan Officer share experience"
                  className="w-full aspect-[16/8]"
                />
              </div>
            </AnimatedSection>

            {/* Usability Testing */}
            <AnimatedSection
              id="usability-testing"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Usability Testing
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                Validated with real users.
              </h2>
              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-8">
                We tested the new flow and the program explainer video to
                validate clarity and usability. Key outcomes:
              </p>

              <div className="flex flex-col gap-6 mb-12">
                {[
                  "Users understood the program basic information",
                  "Used tooltips a lot to answer their questions",
                  "Positive feedback on the guided, step-by-step experience",
                  "Users were confused about the different funding sources (Equity Boost, Equity Unlock). A clear area for follow-up",
                  "Users didn't like the money suit of the video and thought it was sketchy. → We re-shoot the video with different scenario and clothing.",
                ].map((result, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <span className="mt-1 text-[#1D3FE8] font-[Bricolage_Grotesque] font-bold text-sm w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-[#444] text-[0.9375rem] leading-[1.85]">
                      {result}
                    </p>
                  </div>
                ))}
              </div>

              <ImageSlot
                src={mazeImg}
                alt="Maze usability testing heatmaps"
                className="w-full aspect-[16/7]"
                caption="Maze usability testing heatmaps"
              />
            </AnimatedSection>

            {/* Takeaways */}
            <AnimatedSection id="takeaways" className="py-20">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Takeaways
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                Approval is a confidence moment, not just a legal step.
              </h2>
              <div className="flex flex-col gap-6">
                {[
                  {
                    label:
                      "Signing the pre-qualification is a celebratory moment",
                    body: "Reframing the approval step as a milestone — not a formality — changed how we approached the entire flow.",
                  },
                  {
                    label: "Context reduces friction more than simplification",
                    body: "Adding video, onboarding, and structure moved the needle more than stripping the UI down. Users needed to understand before they could commit.",
                  },
                  {
                    label: "Small interaction details reinforce trust",
                    body: "The slide-to-approve gesture made the moment feel intentional and deliberate — increasing engagement without adding complexity.",
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <span className="mt-1 text-[#1D3FE8] font-[Bricolage_Grotesque] font-bold text-sm w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] mb-2">
                        {item.label}
                      </p>
                      <p className="text-[#555] text-[0.9375rem] leading-[1.85]">
                        {item.body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            {/* Footer nav */}
            <div className="border-t border-[#E2E8F0] py-16 flex items-center justify-between">
              <Link
                to="/#work"
                className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
              >
                ← All case studies
              </Link>
              <div className="text-right">
                <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                  Next case study
                </p>
                <Link
                  to="/case-study/notification-wallet"
                  className="font-[Bricolage_Grotesque] font-extrabold text-lg tracking-[-0.02em] text-[#1A1A1A] hover:text-[#1D3FE8] transition-colors"
                >
                  Notification center →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
